import { keepPreviousData } from "@tanstack/react-query";
import { useBookingModuleStore, useBookingModuleStoreActions, useCalendarRoot } from "PFApp/booking/store";
import { isProjects } from "PFApp/booking/utils/calendar_root.utils";
import { formatFiltersV2MetaToApiFormat } from "PFApp/components/filters_v2";
import { FiltersV2StorePage } from "PFApp/components/filters_v2/stores/filters_v2_store/filters_v2_store.types";
import { stringify } from "PFCore/helpers/use_deterministic_stringify";
import { useWorkforceEntries } from "PFCore/hooks/queries/bookings/workforce/use_workforce_entries";
import { useLatestFilters } from "PFCore/hooks/queries/filters/use_latest_filters";
import { useWorkforceMetrics } from "PFCore/hooks/queries/workforce_metrics";
import { Meta } from "PFTypes";
import { PageTarget } from "PFTypes/saved_filters";
import { useEffect } from "react";

type UseWorkforceMetricsData = {
  workforceMeta?: Meta;
};

export const useWorkforceMetricsData = ({ workforceMeta }: UseWorkforceMetricsData): void => {
  const calendarRange = useBookingModuleStore((state) => state.calendarRange);
  const calendarRoot = useCalendarRoot();
  const { setWorkforceMetrics } = useBookingModuleStoreActions();

  const { data: selectedFilters, isFetching: isLoadingFilters } = useLatestFilters({
    target: PageTarget.BookingModuleWorkforce,
    key: FiltersV2StorePage.BookingModule
  });

  const apiFilters =
    (workforceMeta?.filtersV2 && formatFiltersV2MetaToApiFormat(workforceMeta.filtersV2)) ??
    (selectedFilters?.collection && formatFiltersV2MetaToApiFormat(selectedFilters?.collection));
  const shouldLoadProfiles = !workforceMeta?.searchId && isProjects(calendarRoot);

  const { data: profilesFetched, isLoading: isLoadingProfiles } = useWorkforceEntries({
    page: 1,
    perPage: 0,
    order: null,
    filters: apiFilters,
    dateRange: calendarRange,
    previousSearchId: undefined,
    options: {
      enabled: shouldLoadProfiles && !isLoadingFilters
    }
  });

  const searchId = workforceMeta?.searchId ?? profilesFetched?.meta.searchId;
  const { data: workforceMetrics, isLoading } = useWorkforceMetrics({
    calendarRange,
    filters: apiFilters,
    previousSearchId: searchId!,
    options: {
      placeholderData: keepPreviousData,
      enabled: !!searchId && (!shouldLoadProfiles || !isLoadingProfiles)
    }
  });

  useEffect(() => {
    setWorkforceMetrics({
      metrics: workforceMetrics,
      loading: isLoading
    });
  }, [stringify({ workforceMetrics }), isLoading]);
};
